/**
 * CursorUtils - Utility functions for cursor positioning and calculations
 *
 * Handles:
 * - Element positioning and coordinate calculations
 * - Scroll and resize handling
 * - Element finding and selection
 * - Distance calculations
 * - Movement duration calculations
 */
export default class CursorUtils {
  constructor(cursor) {
    this.cursor = cursor
  }

  /**
   * Get element by selector or element reference
   * @param {string|HTMLElement} target - CSS selector or element
   * @returns {HTMLElement|null} - Found element or null
   */
  getElement(target) {
    if (typeof target === 'string') {
      const element = document.querySelector(target)
      return element
    } else if (target instanceof HTMLElement) {
      return target
    }
    return null
  }

  /**
   * Get multiple elements by selector
   * @param {string} selector - CSS selector
   * @returns {NodeList} - Found elements
   */
  getElements(selector) {
    return document.querySelectorAll(selector)
  }

  /**
   * Convert percentage-based position to absolute pixels
   * @param {Object} position - Position object with x/y that can be percentages or pixels
   * @returns {Object} - Position object with absolute pixel values
   */
  convertPercentagePosition(position) {
    if (!position) return null

    const result = { ...position }

    // Convert x coordinate
    if (typeof position.x === 'string' && position.x.endsWith('%')) {
      const percentage = parseFloat(position.x) / 100
      result.x = window.innerWidth * percentage
    }

    // Convert y coordinate
    if (typeof position.y === 'string' && position.y.endsWith('%')) {
      const percentage = parseFloat(position.y) / 100
      result.y = window.innerHeight * percentage
    }

    return result
  }

  /**
   * Calculate relative position from current position
   * @param {Object} currentPosition - Current position {x, y}
   * @param {Object} relativeOffset - Relative offset {x, y}
   * @returns {Object} - New absolute position
   */
  calculateRelativePosition(currentPosition, relativeOffset) {
    if (!currentPosition || !relativeOffset) return null

    return {
      x: currentPosition.x + (relativeOffset.x || 0),
      y: currentPosition.y + (relativeOffset.y || 0),
    }
  }

  /**
   * Get the positioning context (first positioned parent) for the cursor container
   * @returns {Object} - Offset of positioning context {x, y}
   */
  getPositioningContextOffset() {
    // Find the cursor container
    const cursorContainer = document.querySelector('.cursor-manager__container')
    if (!cursorContainer) return { x: 0, y: 0 }

    // Find the first positioned parent (not static)
    let parent = cursorContainer.offsetParent
    if (!parent || parent === document.body) return { x: 0, y: 0 }

    // Get the offset of this positioning context relative to the document
    const rect = parent.getBoundingClientRect()
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft

    return {
      x: rect.left + scrollLeft,
      y: rect.top + scrollTop,
    }
  }

  /**
   * Get element position with enhanced support for different positioning modes
   * @param {string|HTMLElement} target - Target element or selector
   * @param {Object} options - Options for positioning
   * @returns {Object} - Position object {x, y}
   */
  getElementPosition(target, options = {}) {
    const element = typeof target === 'string' ? document.querySelector(target) : target
    if (!element) return null

    // If the element is an annotation, ensure its position is up to date
    if (
      element.hasAttribute('data-controller') &&
      element.getAttribute('data-controller').includes('annotation-enhanced')
    ) {
      const controller = this.cursor.manager.application.getControllerForElementAndIdentifier(
        element,
        'annotation-enhanced'
      )
      if (controller && controller.updatePosition) {
        controller.updatePosition()
      }
    }

    const rect = element.getBoundingClientRect()
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft

    // Get positioning context offset early as we'll need it in multiple places
    const contextOffset = this.getPositioningContextOffset()

    // Handle hidden elements - if element is hidden but we need its position,
    // try to get a reasonable position by temporarily showing it
    if (rect.width === 0 && rect.height === 0 && options.allowHidden) {
      // Try to get position by temporarily making element visible
      const originalStyle = {
        display: element.style.display,
        visibility: element.style.visibility,
        position: element.style.position,
        left: element.style.left,
        top: element.style.top,
      }

      // Temporarily make visible but off-screen
      element.style.display = 'block'
      element.style.visibility = 'hidden'
      element.style.position = 'absolute'
      element.style.left = '-9999px'
      element.style.top = '-9999px'

      const tempRect = element.getBoundingClientRect()

      // Restore original style
      Object.assign(element.style, originalStyle)

      // If we got dimensions, use the original position but with proper center calculation
      if (tempRect.width > 0 && tempRect.height > 0) {
        // Use the original position if it exists, otherwise use parent position
        let parentRect = element.parentElement?.getBoundingClientRect()
        if (!parentRect) parentRect = { left: 0, top: 0 }

        // Estimate position based on parent and element dimensions
        const x = parentRect.left + scrollLeft + tempRect.width / 2 - contextOffset.x
        const y = parentRect.top + scrollTop + tempRect.height / 2 - contextOffset.y

        return { x, y }
      }
    }

    // Calculate document coordinates
    let x = rect.left + scrollLeft + rect.width / 2
    let y = rect.top + scrollTop + rect.height / 2

    // Adjust for positioning context offset
    x -= contextOffset.x
    y -= contextOffset.y

    // Apply positioning options
    if (options.anchor) {
      switch (options.anchor) {
        case 'top-left':
          x = rect.left + scrollLeft - contextOffset.x
          y = rect.top + scrollTop - contextOffset.y
          break
        case 'top-right':
          x = rect.left + scrollLeft + rect.width - contextOffset.x
          y = rect.top + scrollTop - contextOffset.y
          break
        case 'bottom-left':
          x = rect.left + scrollLeft - contextOffset.x
          y = rect.top + scrollTop + rect.height - contextOffset.y
          break
        case 'bottom-right':
          x = rect.left + scrollLeft + rect.width - contextOffset.x
          y = rect.top + scrollTop + rect.height - contextOffset.y
          break
        case 'top-center':
          x = rect.left + scrollLeft + rect.width / 2 - contextOffset.x
          y = rect.top + scrollTop - contextOffset.y
          break
        case 'bottom-center':
          x = rect.left + scrollLeft + rect.width / 2 - contextOffset.x
          y = rect.top + scrollTop + rect.height - contextOffset.y
          break
        case 'center-left':
          x = rect.left + scrollLeft - contextOffset.x
          y = rect.top + scrollTop + rect.height / 2 - contextOffset.y
          break
        case 'center-right':
          x = rect.left + scrollLeft + rect.width - contextOffset.x
          y = rect.top + scrollTop + rect.height / 2 - contextOffset.y
          break
        // 'center' is already calculated correctly above
      }
    }

    // Apply offset if provided
    if (options.offset) {
      x += options.offset.x || 0
      y += options.offset.y || 0
    }

    return { x, y }
  }

  /**
   * Get element bounds relative to document
   * @param {string|HTMLElement} target - CSS selector or element
   * @returns {Object|null} - Bounds {x, y, width, height, top, right, bottom, left} or null
   */
  getElementBounds(target) {
    const element = this.getElement(target)
    if (!element) return null

    const rect = element.getBoundingClientRect()

    return {
      x: rect.left + window.pageXOffset,
      y: rect.top + window.pageYOffset,
      width: rect.width,
      height: rect.height,
      top: rect.top + window.pageYOffset,
      right: rect.right + window.pageXOffset,
      bottom: rect.bottom + window.pageYOffset,
      left: rect.left + window.pageXOffset,
    }
  }

  /**
   * Calculate distance between two points
   * @param {Object} point1 - First point {x, y}
   * @param {Object} point2 - Second point {x, y}
   * @returns {number} - Distance in pixels
   */
  calculateDistance(point1, point2) {
    const dx = point2.x - point1.x
    const dy = point2.y - point1.y
    return Math.sqrt(dx * dx + dy * dy)
  }

  /**
   * Calculate movement duration based on distance
   * @param {number} distance - Distance in pixels
   * @param {Object} options - Options for calculation
   * @param {number} options.minDuration - Minimum duration (ms)
   * @param {number} options.maxDuration - Maximum duration (ms)
   * @param {number} options.speed - Pixels per second
   * @returns {number} - Duration in milliseconds
   */
  calculateMoveDuration(distance, options = {}) {
    const {
      minDuration = 300,
      maxDuration = 3000,
      speed = 800, // pixels per second
    } = options

    const duration = (distance / speed) * 1000
    return Math.max(minDuration, Math.min(maxDuration, duration))
  }

  /**
   * Adjust position for scroll offset
   * @param {Object} position - Position {x, y}
   * @returns {Object} - Adjusted position {x, y}
   */
  adjustForScroll(position) {
    return {
      x: position.x - window.pageXOffset,
      y: position.y - window.pageYOffset,
    }
  }

  /**
   * Get viewport dimensions
   * @returns {Object} - Viewport {width, height}
   */
  getViewportDimensions() {
    return {
      width: window.innerWidth,
      height: window.innerHeight,
    }
  }

  /**
   * Get document dimensions
   * @returns {Object} - Document {width, height}
   */
  getDocumentDimensions() {
    return {
      width: document.documentElement.scrollWidth,
      height: document.documentElement.scrollHeight,
    }
  }

  /**
   * Check if position is within viewport
   * @param {Object} position - Position {x, y}
   * @returns {boolean} - True if position is visible
   */
  isPositionInViewport(position) {
    const viewport = this.getViewportDimensions()
    const adjustedPosition = this.adjustForScroll(position)

    return (
      adjustedPosition.x >= 0 &&
      adjustedPosition.x <= viewport.width &&
      adjustedPosition.y >= 0 &&
      adjustedPosition.y <= viewport.height
    )
  }

  /**
   * Check if element is visible in viewport
   * @param {string|HTMLElement} target - CSS selector or element
   * @returns {boolean} - True if element is visible
   */
  isElementVisible(target) {
    const element = this.getElement(target)
    if (!element) return false

    const rect = element.getBoundingClientRect()
    const viewport = this.getViewportDimensions()

    return rect.top < viewport.height && rect.bottom > 0 && rect.left < viewport.width && rect.right > 0
  }

  /**
   * Check if element is displayed (not hidden with display: none, visibility: hidden, etc.)
   * @param {string|HTMLElement} target - CSS selector or element
   * @returns {boolean} - True if element is displayed and has dimensions
   */
  isElementDisplayed(target) {
    const element = this.getElement(target)
    if (!element) return false

    // Check if element is connected to the DOM
    if (!element.isConnected) return false

    // Get computed style
    const style = window.getComputedStyle(element)

    // Check for display: none
    if (style.display === 'none') return false

    // Check for visibility: hidden
    if (style.visibility === 'hidden') return false

    // Check for opacity: 0 (optional - you might want to allow this)
    // if (style.opacity === '0') return false

    // Check if element has dimensions
    const rect = element.getBoundingClientRect()
    if (rect.width === 0 && rect.height === 0) return false

    return true
  }

  /**
   * Check if element is ready for cursor interaction (displayed and has valid position)
   * @param {string|HTMLElement} target - CSS selector or element
   * @returns {boolean} - True if element is ready for interaction
   */
  isElementReady(target) {
    if (!this.isElementDisplayed(target)) return false

    const element = this.getElement(target)
    const rect = element.getBoundingClientRect()

    // Ensure position is not at 0,0 (which happens with display: none)
    if (rect.left === 0 && rect.top === 0 && rect.width === 0 && rect.height === 0) return false

    return true
  }

  /**
   * Get element center point relative to another element
   * @param {string|HTMLElement} target - Target element
   * @param {string|HTMLElement} relative - Relative element
   * @returns {Object|null} - Relative position {x, y} or null
   */
  getRelativePosition(target, relative) {
    const targetBounds = this.getElementBounds(target)
    const relativeBounds = this.getElementBounds(relative)

    if (!targetBounds || !relativeBounds) return null

    return {
      x: targetBounds.x + targetBounds.width / 2 - relativeBounds.x,
      y: targetBounds.y + targetBounds.height / 2 - relativeBounds.y,
    }
  }

  /**
   * Generate random position within bounds
   * @param {Object} bounds - Bounds {x, y, width, height}
   * @param {Object} margin - Margin {x, y}
   * @returns {Object} - Random position {x, y}
   */
  getRandomPosition(bounds, margin = { x: 20, y: 20 }) {
    const x = bounds.x + margin.x + Math.random() * (bounds.width - 2 * margin.x)
    const y = bounds.y + margin.y + Math.random() * (bounds.height - 2 * margin.y)

    return { x, y }
  }

  /**
   * Get safe position within viewport
   * @param {Object} position - Desired position {x, y}
   * @param {Object} margin - Margin from viewport edges
   * @returns {Object} - Safe position {x, y}
   */
  getSafePosition(position, margin = { x: 20, y: 20 }) {
    const viewport = this.getViewportDimensions()
    const scroll = { x: window.pageXOffset, y: window.pageYOffset }

    const x = Math.max(scroll.x + margin.x, Math.min(position.x, scroll.x + viewport.width - margin.x))

    const y = Math.max(scroll.y + margin.y, Math.min(position.y, scroll.y + viewport.height - margin.y))

    return { x, y }
  }

  /**
   * Find closest element to position
   * @param {Object} position - Position {x, y}
   * @param {string} selector - CSS selector for candidate elements
   * @returns {HTMLElement|null} - Closest element or null
   */
  findClosestElement(position, selector) {
    const elements = this.getElements(selector)
    if (elements.length === 0) return null

    let closestElement = null
    let closestDistance = Infinity

    elements.forEach((element) => {
      const elementPosition = this.getElementPosition(element)
      if (elementPosition) {
        const distance = this.calculateDistance(position, elementPosition)
        if (distance < closestDistance) {
          closestDistance = distance
          closestElement = element
        }
      }
    })

    return closestElement
  }

  /**
   * Check if two rectangles intersect
   * @param {Object} rect1 - First rectangle {x, y, width, height}
   * @param {Object} rect2 - Second rectangle {x, y, width, height}
   * @returns {boolean} - True if rectangles intersect
   */
  rectanglesIntersect(rect1, rect2) {
    return !(
      rect1.x + rect1.width < rect2.x ||
      rect2.x + rect2.width < rect1.x ||
      rect1.y + rect1.height < rect2.y ||
      rect2.y + rect2.height < rect1.y
    )
  }

  /**
   * Get elements that intersect with a position
   * @param {Object} position - Position {x, y}
   * @param {string} selector - CSS selector for candidate elements
   * @param {number} tolerance - Tolerance in pixels
   * @returns {Array} - Array of intersecting elements
   */
  getElementsAtPosition(position, selector, tolerance = 0) {
    const elements = this.getElements(selector)
    const intersecting = []

    elements.forEach((element) => {
      const bounds = this.getElementBounds(element)
      if (bounds) {
        const rect = {
          x: bounds.x - tolerance,
          y: bounds.y - tolerance,
          width: bounds.width + 2 * tolerance,
          height: bounds.height + 2 * tolerance,
        }

        const point = {
          x: position.x,
          y: position.y,
          width: 1,
          height: 1,
        }

        if (this.rectanglesIntersect(rect, point)) {
          intersecting.push(element)
        }
      }
    })

    return intersecting
  }

  /**
   * Wait for specified duration
   * @param {number} duration - Duration in milliseconds
   * @returns {Promise} - Promise that resolves after duration
   */
  wait(duration) {
    return new Promise((resolve) => setTimeout(resolve, duration))
  }

  /**
   * Throttle function execution
   * @param {Function} func - Function to throttle
   * @param {number} wait - Wait time in milliseconds
   * @returns {Function} - Throttled function
   */
  throttle(func, wait) {
    let timeout
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout)
        func(...args)
      }
      clearTimeout(timeout)
      timeout = setTimeout(later, wait)
    }
  }

  /**
   * Debounce function execution
   * @param {Function} func - Function to debounce
   * @param {number} wait - Wait time in milliseconds
   * @returns {Function} - Debounced function
   */
  debounce(func, wait) {
    let timeout
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout)
        func(...args)
      }
      clearTimeout(timeout)
      timeout = setTimeout(later, wait)
    }
  }

  /**
   * Generate CSS selector for element
   * @param {HTMLElement} element - Element to generate selector for
   * @returns {string} - CSS selector
   */
  generateSelector(element) {
    if (element.id) {
      return `#${element.id}`
    }

    if (element.className) {
      const classes = element.className.split(' ').filter((cls) => cls.trim())
      if (classes.length > 0) {
        return `.${classes.join('.')}`
      }
    }

    // Fall back to tag name and nth-child
    const tagName = element.tagName.toLowerCase()
    const siblings = Array.from(element.parentNode.children).filter((child) => child.tagName.toLowerCase() === tagName)

    if (siblings.length > 1) {
      const index = siblings.indexOf(element) + 1
      return `${tagName}:nth-child(${index})`
    }

    return tagName
  }

  /**
   * Get element path (for debugging)
   * @param {HTMLElement} element - Element to get path for
   * @returns {string} - Element path
   */
  getElementPath(element) {
    const path = []
    let current = element

    while (current && current !== document.body) {
      path.unshift(this.generateSelector(current))
      current = current.parentNode
    }

    return path.join(' > ')
  }
}
